import { BreakPoint } from '@/hooks/useBreakPoint/consts';
import useIsMobile from '@/hooks/useIsMobile/useIsMobile';
import { translateJobKey } from '@uikit/helpers/translateJobKey';
import Button from '../Button/Button';
import { ButtonColor, ButtonSize, ShowIcon } from '../Button/consts';
import { ClickableAction } from '../Clickable/consts';
import Icon from '../Icon/Icon';
import StageBox from '../StageBox/StageBox';
import styles from './JobStageBox.module.scss';
import type { JobStageBoxProps } from './interfaces';

const APPLY_BUTTON_LABEL = 'Jetzt bewerben';
const APPLY_BUTTON_LABEL_EMAIL = 'E-Mail-Bewerbung';
const APPLY_BUTTON_LABEL_EXTERNAL = 'Online-Bewerbung';

const JobStageBox = ({
  title,
  jobId,
  primaryLocation,
  timeType,
  url,
  helperText,
  deadlineInfo,
  jobApplicationTarget,
  jobEmailTarget,
  footNote,
}: JobStageBoxProps) => {
  const hasEmailTarget = jobApplicationTarget === 'email' && jobEmailTarget;
  const hasHybridTarget =
    jobApplicationTarget === 'hybridWorkday' && jobEmailTarget;
  const hasHybridExternalTarget =
    jobApplicationTarget === 'hybridExternalTarget' && jobEmailTarget;
  const externalUrlPath =
    jobApplicationTarget === 'externalTarget' || hasHybridExternalTarget
      ? ''
      : '/apply';
  const isMobile = useIsMobile(BreakPoint.LG);

  const baseButtonProps = {
    size: isMobile ? ButtonSize.Medium : ButtonSize.Large,
    color: ButtonColor.Primary,
    className: styles.button,
  };

  const ApplicationTarget = () => {
    if (hasEmailTarget) {
      return (
        <Button
          actionType={ClickableAction.MailTo}
          email={jobEmailTarget || ''} // this can't be undefined because of `hasEmailTarget` but typescript doesn't know that
          {...baseButtonProps}
        >
          {APPLY_BUTTON_LABEL}
        </Button>
      );
    }

    if (hasHybridTarget) {
      return (
        <div className={styles.applyButtons}>
          <Button
            actionType={ClickableAction.Linkextern}
            linkextern={`${url}${externalUrlPath}`}
            showIcon={ShowIcon.ShowIcon}
            icon={'user/pen'}
            {...baseButtonProps}
            className={styles.applyButton}
          >
            {APPLY_BUTTON_LABEL_EXTERNAL}
          </Button>
          <Button
            actionType={ClickableAction.MailTo}
            email={jobEmailTarget || ''} // this can't be undefined because of `hasEmailTarget` but typescript doesn't know that
            showIcon={ShowIcon.ShowIcon}
            icon={'user/mail'}
            {...baseButtonProps}
            color={ButtonColor.Secondary}
            className={styles.applyButton}
          >
            {APPLY_BUTTON_LABEL_EMAIL}
          </Button>
        </div>
      );
    }

    if (hasHybridExternalTarget) {
      return (
        <div className={styles.applyButtons}>
          <Button
            actionType={ClickableAction.Linkextern}
            linkextern={`${url}${externalUrlPath}`}
            showIcon={ShowIcon.ShowIcon}
            icon={'user/pen'}
            {...baseButtonProps}
            className={styles.applyButton}
          >
            {APPLY_BUTTON_LABEL_EXTERNAL}
          </Button>
          <Button
            actionType={ClickableAction.MailTo}
            email={jobEmailTarget || ''} // this can't be undefined because of `hasEmailTarget` but typescript doesn't know that
            showIcon={ShowIcon.ShowIcon}
            icon={'user/mail'}
            {...baseButtonProps}
            color={ButtonColor.Secondary}
            className={styles.applyButton}
          >
            {APPLY_BUTTON_LABEL_EMAIL}
          </Button>
        </div>
      );
    }

    return (
      <Button
        actionType={ClickableAction.Linkextern}
        linkextern={`${url}${externalUrlPath}`}
        {...baseButtonProps}
      >
        {APPLY_BUTTON_LABEL}
      </Button>
    );
  };

  return (
    <StageBox backgroundType="white" hasShadow hasTopLine>
      <div className={styles.base}>
        {jobId && <h2 className={styles.jobId}>Job-ID: {jobId}</h2>}
        <h1 className={styles.title}>{title}</h1>
        {helperText && <span className={styles.helperText}>{helperText}</span>}
        <div className={styles.infos}>
          <div className={styles.infoItem}>
            <span className={styles.infoItemIcon}>
              <Icon size="iconSize24" variant={'user/pin'} />
            </span>
            <span className={styles.infoItemText}>{primaryLocation}</span>
          </div>
          <div className={styles.infoItem}>
            <span className={styles.infoItemIcon}>
              <Icon size="iconSize24" variant={'user/clock'} />
            </span>
            <span className={styles.infoItemText}>
              {translateJobKey(timeType)}
            </span>
          </div>
          {deadlineInfo && (
            <div className={styles.infoItem}>
              <span className={styles.infoItemIcon}>
                <Icon size="iconSize24" variant={'user/information'} />
              </span>
              <span className={styles.infoItemText}>{deadlineInfo}</span>
            </div>
          )}
        </div>
        <div className={styles.buttons}>
          <ApplicationTarget />
        </div>
        <div className={styles.footnote}>
          <div>
            {footNote ||
              'Das Geschlecht ist uns nicht wichtig, entscheidend ist für uns, dass du zu unserer Kultur und ins Team passt und für deinen Job brennst.'}
          </div>
        </div>
      </div>
    </StageBox>
  );
};

export default JobStageBox;
